/**
 * DiarizedTranscript
 *
 * Renders a speaker-separated call transcript as chat-style bubbles.
 *
 * Input shape — `segments` is a list of objects produced by the
 * backend's dual-channel transcription pipeline in
 * `twilio_integration/router.py :: _process_call_recording`:
 *   { speaker_label: "internal"|"external"|"system"|…,
 *     speaker_name:  "Simon Baird"|"Bob Smith"|"System"|…,
 *     speaker_role:  "internal"|"external"|"system",
 *     start: 12.3,   // seconds
 *     end: 15.7,
 *     text: "Hey Bob, got a sec?" }
 *
 * If the record predates dual-channel recording (no `diarized_segments`
 * on the row), falls back to the flat `transcript` string so the
 * legacy calls still render.
 */
import React from 'react';

const fmtClock = (secs) => {
  const s = Math.max(0, Math.floor(Number(secs) || 0));
  const mm = Math.floor(s / 60);
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
};

const roleBubbleStyles = (role) => {
  switch ((role || '').toLowerCase()) {
    case 'internal':
      return {
        align: 'justify-end',
        bubble: 'bg-[#586D52] text-white',
        name: 'text-white/80',
        ts: 'text-white/50',
      };
    case 'external':
      return {
        align: 'justify-start',
        bubble: 'bg-white dark:bg-[#1a1a1a] border text-foreground',
        name: 'text-[#586D52]',
        ts: 'text-muted-foreground',
      };
    case 'system':
    default:
      return {
        align: 'justify-center',
        bubble: 'bg-amber-50 border border-amber-200 text-amber-900 italic',
        name: 'text-amber-700',
        ts: 'text-amber-700/60',
      };
  }
};

export const DiarizedTranscript = ({ segments, fallbackText }) => {
  const hasSegments = Array.isArray(segments) && segments.length > 0;
  if (!hasSegments) {
    if (!fallbackText) {
      return (
        <p className="text-xs text-muted-foreground italic text-center py-6">
          No transcript available.
        </p>
      );
    }
    return (
      <pre
        className="text-xs whitespace-pre-wrap break-words font-mono bg-gray-50 dark:bg-[#252525] rounded-md p-3"
        data-testid="transcript-plain"
      >
        {fallbackText}
      </pre>
    );
  }
  return (
    <div className="space-y-2" data-testid="transcript-diarized">
      {segments.map((s, i) => {
        const styles = roleBubbleStyles(s.speaker_role);
        const isSystem = (s.speaker_role || '').toLowerCase() === 'system';
        return (
          <div key={i} className={`flex ${styles.align}`}>
            <div className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm shadow-sm ${styles.bubble}`}>
              {!isSystem && (
                <p className={`text-[10px] font-bold uppercase tracking-wide ${styles.name}`}>
                  {s.speaker_name || s.speaker_label || 'Unknown'}
                </p>
              )}
              <p className="whitespace-pre-wrap break-words">
                {isSystem ? <em>System: {s.text}</em> : s.text}
              </p>
              <p className={`text-[9px] mt-0.5 ${styles.ts}`}>
                {fmtClock(s.start)}{s.end && s.end !== s.start ? ` – ${fmtClock(s.end)}` : ''}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DiarizedTranscript;
